import React, { useState, useEffect } from 'react';
import { Line, Pie, Bar } from 'react-chartjs-2';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Calendar,
  Target,
  DollarSign,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  Title,
  zoomPlugin // Register zoom plugin
);



const TIMEFRAMES = [
  { label: '1W', days: 7, icon: Calendar },
  { label: '1M', days: 30, icon: Calendar },
  { label: '1Y', days: 365, icon: Calendar },
];

const Analytics = () => {
  const [accountValueData, setAccountValueData] = useState({
    labels: [],
    datasets: [{
      label: 'Account Value (£)',
      data: [],
      borderColor: '#10b981',
      backgroundColor: 'rgba(16,185,129,0.1)',
      fill: true,
      tension: 0.4,
    }],
    markers: []
  });
  
  const [sectorData, setSectorData] = useState({
    labels: [],
    datasets: [{
      label: 'Invested Capital (£)',
      data: [],
      backgroundColor: [
        '#3b82f6', '#10b981', '#f59e42', '#ef4444', 
        '#a78bfa', '#fbbf24', '#06b6d4', '#8b5cf6'
      ],
      borderWidth: 1,
    }],
    breakdown: []
  });

  const [performanceMetrics, setPerformanceMetrics] = useState({
    bestPerformer: '',
    worstPerformer: '',
    totalTrades: 0,
    winRate: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTimeframe, setSelectedTimeframe] = useState(TIMEFRAMES[1]); // default 1M

  useEffect(() => {
    fetchAnalyticsData(selectedTimeframe.days);
  }, [selectedTimeframe]);



  // Helper to fill missing dates in account value data
  function fillMissingDates(data, days) {
    if (!data.labels || !data.datasets || !data.datasets[0].data) return data;
    const filledLabels = [];
    const filledData = [];
    const today = new Date();
    let lastValue = null;
    let labelIdx = 0;
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const label = d.toISOString().slice(0, 10); // 'YYYY-MM-DD'
      filledLabels.push(label);
      if (data.labels[labelIdx] === label) {
        lastValue = data.datasets[0].data[labelIdx];
        filledData.push(lastValue);
        labelIdx++;
      } else {
        filledData.push(lastValue !== null ? lastValue : 0);
      }
    }
    return {
      ...data,
      labels: filledLabels,
      datasets: [{ ...data.datasets[0], data: filledData }],
    };
  }

  // Helper to aggregate by month for 1Y view
  function aggregateByMonth(data) {
    if (!data.labels || !data.datasets || !data.datasets[0].data) return data;
    const monthly = {};
    data.labels.forEach((label, idx) => {
      const month = label.slice(0, 7); // 'YYYY-MM'
      monthly[month] = data.datasets[0].data[idx]; // last value in month
    });
    const months = Object.keys(monthly).sort();
    return {
      ...data,
      labels: months,
      datasets: [{ ...data.datasets[0], data: months.map(m => monthly[m]) }],
    };
  }


  const fetchAnalyticsData = async (days = 30) => {
    try {
      setLoading(true);
      setError('');

      // Fetch account value chart data
      const accountResponse = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/analytics/account-value-chart/?days=${days}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      // Fetch sector allocation data
      const sectorResponse = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/analytics/sector-allocation-chart/`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      // Fetch portfolio data for real metrics
      const portfolioResponse = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/trading/portfolio/`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      if (accountResponse.data && sectorResponse.data) {
        let filledAccountValueData = fillMissingDates(accountResponse.data, days);
        // If 1Y and not enough daily data, aggregate by month
        if (days === 365 && filledAccountValueData.labels.length > 0) {
          // If there are less than 300 daily points, aggregate by month
          if (filledAccountValueData.labels.length < 300) {
            filledAccountValueData = aggregateByMonth(filledAccountValueData);
          }
        }
        setAccountValueData(filledAccountValueData);
        setSectorData(sectorResponse.data);
        
        // Calculate trading metrics from portfolio data
        let totalTrades = 0;
        let winRate = 0;
        let bestPerformer = '';
        let worstPerformer = '';

        if (portfolioResponse.data?.success && portfolioResponse.data.data?.holdings) {
          const holdings = portfolioResponse.data.data.holdings;
          
          // Get actual transaction count from transaction history
          try {
            console.log('Fetching transaction history...');
            const transactionResponse = await axios.get('/api/v1/trading/transactions/', {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            console.log('Transaction response:', transactionResponse.data);
            
            if (transactionResponse.data?.success && transactionResponse.data.data) {
              totalTrades = transactionResponse.data.data.length;
              console.log('Total trades from API:', totalTrades);
            } else {
              console.log('Transaction API response format unexpected:', transactionResponse.data);
              // Fallback: count holdings as trades
              totalTrades = holdings.length;
            }
          } catch (error) {
            console.error('Failed to fetch transaction count:', error);
            console.error('Error details:', error.response?.data);
            // Fallback: count holdings as trades (not ideal but better than summing quantities)
            totalTrades = holdings.length;
          }
          
          // Calculate win rate based on profitable vs unprofitable positions
          const profitableHoldings = holdings.filter(holding => 
            (holding.unrealized_gain_loss || 0) >= 0
          );
          winRate = holdings.length > 0 ? (profitableHoldings.length / holdings.length) * 100 : 0;
          
          // Find best and worst performers
          if (holdings.length > 0) {
            const sortedByGain = [...holdings].sort((a, b) => 
              (b.unrealized_gain_loss || 0) - (a.unrealized_gain_loss || 0)
            );
            bestPerformer = sortedByGain[0]?.symbol || '';
            worstPerformer = sortedByGain[sortedByGain.length - 1]?.symbol || '';
          }
        }
        
        setPerformanceMetrics({
          bestPerformer,
          worstPerformer,
          totalTrades,
          winRate: Math.round(winRate)
        });
      }

    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };





  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="relative">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Analytics Dashboard</h1>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Comprehensive performance insights and portfolio analysis
              </p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400 font-medium">Loading analytics data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        {/* Header */}
        <div className="relative">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Analytics Dashboard</h1>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Comprehensive performance insights and portfolio analysis
              </p>
            </div>
          </div>
        </div>

        {/* Error State */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <AlertCircle className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">Error Loading Analytics</h3>
              <p className="text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
          </div>
          <button 
            onClick={fetchAnalyticsData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="relative">
                 <div className="flex items-center justify-between">
           <div>
             <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Analytics Dashboard</h1>
             <p className="text-gray-600 dark:text-gray-400 text-lg">
               Comprehensive performance insights and portfolio analysis
             </p>
           </div>
         </div>
      </div>

             {/* Performance Metrics Cards */}
       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
         {/* Total Trades */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
          <div className="relative">
            <div className="flex items-center space-x-3 mb-2">
              <Activity className="h-6 w-6" />
              <span className="text-blue-100 text-sm font-medium">Total Trades</span>
            </div>
            <p className="text-2xl font-bold">{performanceMetrics.totalTrades}</p>
            <p className="text-sm text-blue-200 mt-1">
              {performanceMetrics.totalTrades > 0 ? 'This period' : 'No trades yet'}
            </p>
          </div>
        </div>

        {/* Win Rate */}
        <div className="relative overflow-hidden bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
          <div className="relative">
            <div className="flex items-center space-x-3 mb-2">
              <Target className="h-6 w-6" />
              <span className="text-purple-100 text-sm font-medium">Win Rate</span>
            </div>
            <p className="text-2xl font-bold">{performanceMetrics.winRate}%</p>
            <p className="text-sm text-purple-200 mt-1">
              {performanceMetrics.totalTrades > 0 ? 'Successful trades' : 'No trades yet'}
            </p>
          </div>
        </div>

        {/* Best Performer */}
        <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white shadow-lg">
          <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full"></div>
          <div className="relative">
            <div className="flex items-center space-x-3 mb-2">
              <CheckCircle className="h-6 w-6" />
              <span className="text-amber-100 text-sm font-medium">Best Performer</span>
            </div>
            <p className="text-xl font-bold">
              {performanceMetrics.bestPerformer || 'N/A'}
            </p>
            <p className="text-sm text-amber-200 mt-1">
              {performanceMetrics.bestPerformer ? 'Top gainer' : 'No positions'}
            </p>
          </div>
        </div>
      </div>

      {/* Account Value Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Account Value Performance</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Track your portfolio growth over time
              </p>
            </div>
            <div className="flex gap-2">
              {TIMEFRAMES.map(tf => (
                <button
                  key={tf.label}
                  className={`px-4 py-2 rounded-lg font-medium border transition-all duration-200 ${
                    selectedTimeframe.label === tf.label 
                      ? 'bg-blue-600 text-white border-blue-600 shadow-lg' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300'
                  }`}
                  onClick={() => setSelectedTimeframe(tf)}
                >
                  <div className="flex items-center space-x-2">
                    <tf.icon className="h-4 w-4" />
                    <span>{tf.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {accountValueData.labels.length > 0 ? (
            <div className="h-80">
              <Line data={accountValueData} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  title: { display: false },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    titleColor: '#fff',
                    bodyColor: '#fff',
                    borderColor: '#3b82f6',
                    borderWidth: 1,
                    cornerRadius: 8,
                    callbacks: {
                      label: function(context) {
                        return `Account Value: ${formatCurrency(context.parsed.y)}`;
                      }
                    }
                  },
                  zoom: {
                    pan: {
                      enabled: true,
                      mode: 'x',
                    },
                    zoom: {
                      wheel: {
                        enabled: true,
                      },
                      pinch: {
                        enabled: true,
                      },
                      mode: 'x',
                    },
                    limits: {
                      x: { minRange: 3 },
                    },
                  },
                },
                scales: {
                  x: {
                    grid: {
                      color: 'rgba(156, 163, 175, 0.1)',
                    },
                    ticks: {
                      color: '#6b7280',
                    }
                  },
                  y: { 
                    beginAtZero: false,
                    grid: {
                      color: 'rgba(156, 163, 175, 0.1)',
                    },
                    ticks: {
                      color: '#6b7280',
                      callback: function(value) {
                        return formatCurrency(value);
                      }
                    }
                  },
                },
                elements: {
                  point: {
                    radius: 4,
                    hoverRadius: 6,
                    backgroundColor: '#3b82f6',
                    borderColor: '#fff',
                    borderWidth: 2,
                  },
                  line: {
                    borderWidth: 3,
                  }
                }
              }} />
            </div>
          ) : (
            <div className="text-center py-16">
              <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Data Available</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Start trading to see your performance analytics here.
              </p>
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium">
                Start Trading
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sector Allocation */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Portfolio Allocation</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Sector distribution and investment breakdown
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {sectorData.labels.length} sectors
              </span>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {sectorData.labels.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Pie Chart */}
              <div className="flex justify-center">
                <div className="w-full max-w-md">
                  <Pie data={sectorData} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { 
                        position: 'bottom',
                        labels: {
                          padding: 20,
                          usePointStyle: true,
                          pointStyle: 'circle'
                        }
                      },
                      title: { display: false },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        borderColor: '#3b82f6',
                        borderWidth: 1,
                        cornerRadius: 8,
                        callbacks: {
                          label: function(context) {
                            return `${context.label}: ${formatCurrency(context.parsed)}`;
                          }
                        }
                      }
                    },
                  }} height={300} />
                </div>
              </div>

              {/* Sector Breakdown */}
              <div>
                <h3 className="font-semibold mb-4 text-gray-900 dark:text-white">Sector Breakdown</h3>
                <div className="space-y-3">
                  {sectorData.breakdown.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: sectorData.datasets[0].backgroundColor[index] }}
                        ></div>
                        <span className="font-medium text-gray-900 dark:text-white">{item.sector}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900 dark:text-white">{formatCurrency(item.amount)}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {((item.amount / sectorData.breakdown.reduce((sum, i) => sum + i.amount, 0)) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-16">
              <PieChart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Allocation Data</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Start investing across different sectors to see your portfolio allocation.
              </p>
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium">
                Explore Sectors
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics; 